import type { Round } from "types";
import { toFiniteNumber } from "utils/presaleDisplay";

export type SaleProgressSource = "sale_progress_api" | "round_derived";

export type SaleProgressMetrics = {
  raisedUsd: number;
  targetUsd: number;
  percent: number;
  source: SaleProgressSource;
};

/**
 * USD raised / cap for the presale progress UI.
 *
 * 1. If `/api/sale/progress` populated `appStore` with `totalPool > 0`, treat `sold` and `totalPool`
 *    as money amounts (USD) from the backend.
 * 2. Otherwise derive from `currentRound`: `sold`/`pool` tokens × `tokenPrice`.
 */
export function computeSaleProgressMetrics(
  currentRound: Round | null,
  saleSoldFromApi: number,
  saleTotalFromApi: number,
): SaleProgressMetrics {
  const tokenPrice = toFiniteNumber(currentRound?.tokenPrice) ?? 0;
  const roundSold = Number(currentRound?.sold || 0);
  const roundPool = Number(currentRound?.pool || 0);
  const fromRoundRaised = roundSold * tokenPrice;
  const fromRoundTarget = roundPool * tokenPrice;

  const apiTotal = Number(saleTotalFromApi) || 0;
  const apiSold = Number(saleSoldFromApi) || 0;

  if (apiTotal > 0) {
    return {
      raisedUsd: apiSold,
      targetUsd: apiTotal,
      percent: Math.min(100, (apiSold / apiTotal) * 100),
      source: "sale_progress_api",
    };
  }

  return {
    raisedUsd: fromRoundRaised,
    targetUsd: fromRoundTarget,
    percent:
      fromRoundTarget > 0
        ? Math.min(100, (fromRoundRaised / fromRoundTarget) * 100)
        : 0,
    source: "round_derived",
  };
}
